import Dexie, { type EntityTable } from 'dexie'

interface CacheValueObject {
  [key: string]: CacheValue
}

export type CacheValue =
  | undefined
  | null
  | string
  | number
  | boolean
  | CacheValueObject
  | Array<CacheValue>

type CacheEntry = {
  key: string
  createdAt: Date
  value: CacheValue
}

class Cache {
  private static readonly DATABASE_NAME = 'Vinted'

  private static readonly TTL_DAYS = 7

  private db: Dexie & { cache: EntityTable<CacheEntry, 'key'> }

  public constructor() {
    this.db = new Dexie(Cache.DATABASE_NAME) as Dexie & { cache: EntityTable<CacheEntry, 'key'> }
    this.db.version(1).stores({
      cache: 'key,createdAt',
    })
  }

  public async get<T extends CacheValue>(key: string): Promise<T | null> {
    const entry = await this.db.cache.get(key)
    if (!entry) {
      return null
    }

    return entry.value as T
  }

  public async set(key: string, value: CacheValue): Promise<void> {
    await this.db.cache.put({ key, createdAt: new Date(), value })
  }

  public async clear(): Promise<void> {
    await this.db.cache.clear()
  }

  public async deleteOutdated(): Promise<void> {
    await this.db.cache
      .where('createdAt')
      .below(new Date(Date.now() - Cache.TTL_DAYS * 24 * 60 * 60 * 1000))
      .delete()
  }
}

export const cache = new Cache()
